/**
 * @file global-app-config.schema.ts
 * @description Configuração global do app Eva (singleton).
 *
 * Esta é a "tela padrão" da Eva — usada como:
 *  - Layout exibido para usuárias sem `partnerId` (ambiente Eva puro)
 *  - Fallback inicial para parceiros que ainda não personalizaram a própria home
 *
 * É um documento único no Firestore com id fixo `default`.
 */

import { z } from 'zod';
import { AppUiPreferencesSchema, HomeScreenLayoutSchema } from './partner.schema';
import { PartnerSchedulingConfigSchema } from './scheduling-common.schema';

const GlobalAppConfigSchema = z.object({
  /** Singleton — sempre `default`. */
  id: z.literal('default'),

  /** Layout master da home — herdado por parceiros que ainda não personalizaram. */
  homeScreenLayout: HomeScreenLayoutSchema.optional(),

  /** Layout master da tela de Conteúdos — herdado por parceiros. */
  contentScreenLayout: HomeScreenLayoutSchema.optional(),

  /** Layout master da tela de Cuidado — herdado por parceiros. */
  careScreenLayout: HomeScreenLayoutSchema.optional(),

  /** Preferências globais de UI master. */
  appUiPreferences: AppUiPreferencesSchema.optional(),

  /**
   * Configuração de agendamento da Eva (cards de preparo, janela, lembretes).
   * Espelha `partner.schedulingConfig`; aqui vale para usuárias sem partnerId.
   */
  schedulingConfig: PartnerSchedulingConfigSchema.optional(),

  /**
   * Os documentos que o cadastro manda aceitar (A062, fase C): a versão
   * vigente e o endereço de cada um. A versão é gravada na conta junto com o
   * instante do aceite (`termosAceitos`). Universal, não por marca (A052,
   * decisão 6). Só a mantenedora altera.
   */
  documentosLegais: z
    .object({
      versao: z.string().min(1),
      termosUrl: z.string().url(),
      privacidadeUrl: z.string().url(),
    })
    .optional(),

  updatedAt: z.coerce.date().optional(),
});

type GlobalAppConfig = z.infer<typeof GlobalAppConfigSchema>;

export { GlobalAppConfigSchema };
export type { GlobalAppConfig };
